"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Clock, ArrowRight } from "lucide-react";
function LoadingSkeleton() {
  return <div className="flex flex-col gap-12">
      <div className="flex flex-col lg:flex-row gap-8 items-center animate-pulse">
        <div className="w-full lg:w-2/3 aspect-video bg-muted rounded-3xl"></div>
        <div className="w-full lg:w-1/3 flex flex-col gap-4">
          <div className="w-24 h-6 bg-muted rounded-lg"></div>
          <div className="w-full h-10 bg-muted rounded-lg"></div>
          <div className="w-3/4 h-10 bg-muted rounded-lg"></div>
          <div className="w-full h-20 bg-muted rounded-lg mt-4"></div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[1, 2, 3].map((_, i) => <div key={i} className="flex flex-col gap-4 animate-pulse" data-index={i}>
            <div className="w-full aspect-[4/3] bg-muted rounded-2xl"></div>
            <div className="w-20 h-5 bg-muted rounded-lg"></div>
            <div className="w-full h-6 bg-muted rounded-lg"></div>
            <div className="w-full h-16 bg-muted rounded-lg"></div>
          </div>)}
      </div>
    </div>;
}
function BlogPostsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) return <LoadingSkeleton />;
  if (error) {
    return <div className="text-center py-12 bg-muted/30 rounded-2xl border border-border">
        <p className="text-muted-foreground">خطا در بارگذاری مقالات. لطفاً بعداً تلاش کنید.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-12 bg-muted/30 rounded-2xl border border-border">
        <p className="text-muted-foreground">در حال حاضر مقاله‌ای برای نمایش وجود ندارد.</p>
      </div>;
  }
  const featuredPost = posts[0];
  const secondaryPosts = posts.slice(1);
  return <div className="flex flex-col gap-12">
      {featuredPost && <Link to={featuredPost.link || "#"} className="group block" data-index={0}>
          <div className="flex flex-col lg:flex-row gap-8 items-center">
            <div className="w-full lg:w-2/3">
              <AspectRatio ratio={16 / 9} className="overflow-hidden rounded-3xl shadow-lg">
                <img src={featuredPost.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1684933/img/psychology_featured_article.png"} data-wvc-srcset={featuredPost.featuredImage ? undefined : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1684933/img/psychology_featured_article.png-wvc-srcset"} data-wvc-sizes={featuredPost.featuredImage ? undefined : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1684933/img/psychology_featured_article.png-wvc-sizes"} alt={featuredPost.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              </AspectRatio>
            </div>
            <div className="w-full lg:w-1/3 flex flex-col gap-5">
              <div className="flex items-center gap-3">
                <Badge className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                  {featuredPost.categories?.[0]?.name || "مقاله ویژه"}
                </Badge>
                <span className="text-sm text-muted-foreground flex items-center gap-1.5 font-medium">
                  <Clock className="w-4 h-4" /> ۵ دقیقه مطالعه
                </span>
              </div>
              <h3 className="text-3xl md:text-4xl font-default font-bold text-foreground group-hover:text-secondary transition-colors leading-tight">
                {featuredPost.title}
              </h3>
              <p className="text-muted-foreground text-lg line-clamp-3 leading-relaxed">
                {featuredPost.excerpt?.replace(/(<([^>]+)>)/gi, "") || "جدیدترین یافته‌ها و راهکارهای تخصصی در زمینه سلامت روان، مدیریت اضطراب و بهبود روابط فردی را از زبان متخصصین ما بخوانید."}
              </p>
              <div className="mt-2 text-secondary font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                مطالعه کامل مقاله <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>}

      {secondaryPosts.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {secondaryPosts.map((post, i) => <Link key={post.id} to={post.link || "#"} className="group block h-full" data-index={i}>
              <Card className="h-full overflow-hidden border border-border/50 shadow-md hover:shadow-lg transition-all duration-300 bg-card flex flex-col rounded-3xl">
                <div className="p-4 pb-0">
                  <AspectRatio ratio={4 / 3} className="overflow-hidden rounded-2xl">
                    <img src={post.featuredImage || `/img/psychology_article_thumb_${i + 1}.png`} data-wvc-srcset={post.featuredImage ? undefined : `/img/psychology_article_thumb_${i + 1}.png-wvc-srcset`} data-wvc-sizes={post.featuredImage ? undefined : `/img/psychology_article_thumb_${i + 1}.png-wvc-sizes`} alt={post.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  </AspectRatio>
                </div>
                <CardHeader className="gap-3 pt-6">
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="rounded-lg bg-secondary/10 text-secondary hover:bg-secondary/20 px-2.5 py-0.5 text-xs font-semibold">
                      {post.categories?.[0]?.name || "روانشناسی"}
                    </Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1.5 font-medium">
                      <Clock className="w-3.5 h-3.5" /> ۵ دقیقه مطالعه
                    </span>
                  </div>
                  <CardTitle className="text-xl font-default group-hover:text-secondary transition-colors line-clamp-2 leading-snug">
                    {post.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <p className="text-muted-foreground text-sm line-clamp-3 leading-relaxed">
                    {post.excerpt?.replace(/(<([^>]+)>)/gi, "") || "آموزش نکات کلیدی و کاربردی برای حفظ سلامت روان، افزایش تاب‌آوری و بهبود کیفیت زندگی در شرایط مختلف."}
                  </p>
                </CardContent>
              </Card>
            </Link>)}
        </div>}
    </div>;
}
export default function ExpertAnimalAdvice() {
  const wp_query_params = {
    post_type: "post",
    per_page: 4,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="265097" id="مجله-روانشناسی-دکتر-اسماعیلی" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-3xl bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-3xl bg-secondary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="mb-12 md:mb-16 max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-4 tracking-tight">
            مجله روانشناسی دکتر اسماعیلی
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed font-default">
            محتوای آموزشی در زمینه‌های سلامت روان، مدیریت اضطراب، فرزندپروری و روانشناسی دردهای مزمن برای ارتقای آگاهی جامعه.
          </p>
        </div>
        
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 4,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <BlogPostsConsumer />
        </PostsProvider>
      </div>
    </section>;
}